// @src/pre-deployment/src/components/PasswordSetup.js v1.0
// - Password setup page for pre-deployment environment
// - Handles email verification and password creation
// - Integrates with Firebase authentication

import { isSignInWithEmailLink } from 'firebase/auth';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../services/firebase';

const PasswordSetup = () => {
  const [email, setEmail] = useState(localStorage.getItem('emailForSignIn') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  
  const { completeEmailVerification, setupPassword, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    console.group('🔐 PRE-DEPLOYMENT PASSWORD SETUP: Component mounted');
    console.log('Current URL:', window.location.href);
    console.log('Email from localStorage:', localStorage.getItem('emailForSignIn'));
    console.log('Current user:', user?.uid);
    
    const checkEmailLink = async () => {
      // Priority 1: Check if user is already authenticated and just needs password setup
      if (user && user.email) {
        console.log('User already authenticated, checking password status');
        setEmail(user.email);
        setEmailVerified(true);
        setMessage('Email verified! Please set your password to continue.');
        return;
      }
      
      // Priority 2: Handle email verification link
      if (isSignInWithEmailLink(auth, window.location.href)) {
        console.log('Valid email link detected');
        
        if (!email) {
          setMessage('Please enter the email address you used to sign up.');
          return;
        }
        
        setIsLoading(true);
        try {
          const result = await completeEmailVerification(email);
          
          if (result.success) {
            setEmailVerified(true);
            setMessage('Email verified! Please set your password to continue.');
            // Clear the URL to prevent reprocessing
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            // Handle specific error cases
            if (result.error.includes('invalid-action-code')) {
              setMessage('This sign-in link has already been used. If you need to set a password, you can continue below.');
              setEmailVerified(true); // Allow password setup even if link is expired
            } else if (result.error.includes('already registered')) {
              setMessage('This email is already registered. Redirecting to login...');
              setTimeout(() => navigate('/'), 3000);
            } else {
              setMessage(`Error: ${result.error}`);
            }
          }
        } catch (error) {
          console.error('Email verification error:', error);
          setMessage('Verification failed, but you can continue with password setup if already signed in.');
          if (user) {
            setEmailVerified(true);
          }
        }
        setIsLoading(false);
      } else if (!user) {
        // Priority 3: No user and no valid link
        console.log('No user and no valid email link');
        setMessage('Please use the registration link from your email, or go back to register again.');
      }
    };
    
    // Add a small delay to let auth state settle
    const timeoutId = setTimeout(checkEmailLink, 500);
    
    console.groupEnd();
    
    return () => clearTimeout(timeoutId);
  }, [completeEmailVerification, navigate, user, email]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    console.group('📧 PRE-DEPLOYMENT PASSWORD SETUP: Email verification');
    console.log('Attempting email verification with:', email);
    
    setIsLoading(true);
    const result = await completeEmailVerification(email);
    
    if (result.success) {
      setEmailVerified(true);
      setMessage('Email verified! Please set your password to continue.');
    } else {
      setMessage(`Error: ${result.error}`);
    }
    setIsLoading(false);
    console.groupEnd();
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    console.group('🔐 PRE-DEPLOYMENT PASSWORD SETUP: Password setup');
    
    if (password !== confirmPassword) {
      setMessage('Passwords do not match. Please try again.');
      return;
    }
    
    if (password.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      return;
    }
    
    setIsLoading(true);
    setMessage('');
    
    try {
      const result = await setupPassword(password);
      
      if (result.success) {
        setMessage('Password set successfully! Proceeding to Korean assessment.');
        console.log('Password setup successful, redirecting to assessment');
        setTimeout(() => navigate('/assessment'), 2000);
      } else {
        if (result.requiresReauth) {
          setMessage(result.error);
        } else {
          setMessage(`Error: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('Password setup error:', error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
      console.groupEnd();
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setShowPassword(!showPassword);
    } else {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  const goBack = () => {
    navigate('/');
  };

  return (
    <div className="container">
      <div className="logo-section">
        <img src="/img/logo.png" alt="Logo" className="logo" />
        <h1>Pre-Deployment</h1>
        <p>Setup Password</p>
      </div>
      
      <div className="auth-section">
        <div className="navigation-controls">
          <button className="nav-btn secondary" onClick={goBack}>
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
        </div>

        <h2>Complete Registration</h2>
        
        {!emailVerified ? (
          <form onSubmit={handleEmailSubmit}>
            <input
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
            />
            <button type="submit" disabled={isLoading || !email}>
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>
        ) : (
          <form onSubmit={handlePasswordSubmit}>
            <div className="password-container">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Create Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                minLength="6"
              />
              <i 
                className={`fas ${showPassword ? 'fa-eye' : 'fa-eye-slash'} password-toggle`}
                onClick={() => togglePasswordVisibility('password')}
              ></i>
            </div>
            
            <div className="password-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                minLength="6"
              />
              <i 
                className={`fas ${showConfirmPassword ? 'fa-eye' : 'fa-eye-slash'} password-toggle`}
                onClick={() => togglePasswordVisibility('confirm')}
              ></i>
            </div>
            
            <button type="submit" disabled={isLoading || !password || !confirmPassword}>
              {isLoading ? 'Setting Password...' : 'Complete Registration'}
            </button>
          </form>
        )}

        {message && (
          <div className={`message ${message.includes('Success') || message.includes('verified') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default PasswordSetup;
